import { useEffect, useRef } from "react";
import "@/App.css";

const ShieldIcon = () => (
  <svg className="nav-shield" viewBox="0 0 22 22" fill="none">
    <rect x="0.5" y="0.5" width="21" height="21" rx="1" stroke="#B41230" strokeWidth="0.8"/>
    <path d="M11 3.5L17 7.5V12.5C17 15.5 14.5 17.8 11 18.5C7.5 17.8 5 15.5 5 12.5V7.5L11 3.5Z"
          fill="#B41230" fillOpacity="0.12" stroke="#B41230" strokeWidth="0.9"/>
    <polyline points="8.5,11 10.5,13 13.5,9.5" stroke="#B41230" strokeWidth="1.2"
              strokeLinecap="round" strokeLinejoin="round" fill="none"/>
  </svg>
);

const Navigation = () => (
  <nav data-testid="main-navigation" aria-label="Main navigation">
    <div className="nav-left">
      <a href="/" className="nav-wordmark" aria-label="Vigil Security Home">
        <ShieldIcon />
        <span className="nav-name">Vigil</span>
      </a>
      <div className="nav-divider" aria-hidden="true"></div>
      <span className="nav-org">Security Standards</span>
    </div>

    <ul className="nav-links" role="menubar">
      <li role="none"><a href="#specs" data-testid="nav-standards" role="menuitem">Standards</a></li>
      <li role="none"><a href="#lab" data-testid="nav-lab" role="menuitem">Security Lab</a></li>
      <li role="none"><a href="#regulatory" data-testid="nav-regulatory" role="menuitem">Regulatory</a></li>
      <li role="none"><a href="#opensource" data-testid="nav-opensource" role="menuitem">Open Source</a></li>
      <li role="none"><a href="#contact" data-testid="nav-contact" role="menuitem">Contact</a></li>
      <li role="none"><a href="#about" data-testid="nav-about" role="menuitem">About</a></li>
    </ul>
  </nav>
);

const Masthead = () => (
  <section id="masthead" data-testid="masthead-section" aria-labelledby="masthead-title">
    <div className="masthead-inner">
      <header className="masthead-left">
        <p className="masthead-kicker">Open Standards Initiative</p>
        <h1 id="masthead-title" className="masthead-headline">The accountability layer <em>AI agents need before they scale.</em></h1>
        <p className="masthead-body">Vigil Security publishes open specifications for AI agent audit trails, trust attestation, and agent revocation. Free to use, without restriction. Built so that the technical standard exists before regulation requires it.</p>
        <div className="masthead-actions">
          <a href="#specs" className="btn-ink" data-testid="btn-read-specs">Read the Specifications →</a>
          <a href="#lab" className="btn-text-link" data-testid="btn-security-lab">Security Lab →</a>
        </div>
      </header>

      <aside className="masthead-right" aria-label="Standards overview">
        <div className="standards-index" role="list">
          <article className="std-row" role="listitem">
            <abbr className="std-abbr" title="Vigil Open Audit Format">VOAF</abbr>
            <div>
              <h2 className="std-title">Vigil Open Audit Format</h2>
              <p className="std-desc">JSON specification for exportable, cryptographically verifiable AI interaction audit trails.</p>
              <span className="std-status live">v1.0 · Live</span>
            </div>
          </article>
          <article className="std-row" role="listitem">
            <abbr className="std-abbr" title="Trust Attestation Protocol">TAP</abbr>
            <div>
              <h2 className="std-title">Trust Attestation Protocol</h2>
              <p className="std-desc">Bidirectional trust certificates for agent-to-agent interactions.</p>
              <span className="std-status live">v1.0 · Live</span>
            </div>
          </article>
          <article className="std-row" role="listitem">
            <abbr className="std-abbr" title="Vigil Agent Revocation Protocol">VARP</abbr>
            <div>
              <h2 className="std-title">Vigil Agent Revocation Protocol</h2>
              <p className="std-desc">Cascading revocation mechanism for compromised agents in multi-agent chains.</p>
              <span className="std-status">v0.8 · Draft</span>
            </div>
          </article>
          <article className="std-row" role="listitem">
            <abbr className="std-abbr" title="Vigil Decomposition Taxonomy">VDT</abbr>
            <div>
              <h2 className="std-title">Vigil Decomposition Taxonomy</h2>
              <p className="std-desc">Open taxonomy for classifying AI interactions across action, scope, and agency dimensions.</p>
              <span className="std-status live">v1.0 · Live</span>
            </div>
          </article>
        </div>
      </aside>
    </div>
  </section>
);

const VOAFCode = () => (
  <pre>{`{
  `}<span className="code-key">"voaf_version"</span>{`: `}<span className="code-str">"1.0"</span>{`,
  `}<span className="code-key">"export_id"</span>{`:    `}<span className="code-str">"exp_01HXK7..."</span>{`,
  `}<span className="code-key">"generated_at"</span>{`: `}<span className="code-str">"2026-03-10T09:14:22Z"</span>{`,
  `}<span className="code-key">"device_id"</span>{`:    `}<span className="code-str">"dvc_sha256:3f4a..."</span>{`,  `}<span className="code-comment">// hashed — not PII</span>{`
  `}<span className="code-key">"interactions"</span>{`: [
    {
      `}<span className="code-key">"id"</span>{`:             `}<span className="code-str">"int_01HXK7..."</span>{`,
      `}<span className="code-key">"ts"</span>{`:             `}<span className="code-str">"2026-03-10T09:12:01.441Z"</span>{`,
      `}<span className="code-key">"provider"</span>{`:      `}<span className="code-str">"anthropic"</span>{`,
      `}<span className="code-key">"action_class"</span>{`:  `}<span className="code-str">"FINANCIAL.TRANSFER"</span>{`,
      `}<span className="code-key">"scope"</span>{`:         `}<span className="code-str">"FINANCIAL"</span>{`,
      `}<span className="code-key">"agency_level"</span>{`:  `}<span className="code-num">0.82</span>{`,
      `}<span className="code-key">"anomaly_score"</span>{`: `}<span className="code-num">0.14</span>{`,
      `}<span className="code-key">"policy_outcome"</span>{`:`}<span className="code-str">"ALLOWED"</span>{`,
      `}<span className="code-key">"hash"</span>{`:          `}<span className="code-str">"sha256:8f2c1..."</span>{`,
      `}<span className="code-key">"prev_hash"</span>{`:     `}<span className="code-str">"sha256:7a1b9..."</span>{`  `}<span className="code-comment">// chain link</span>{`
    }
  ],
  `}<span className="code-key">"chain_root"</span>{`: `}<span className="code-str">"sha256:2d9e4..."</span>{`,
  `}<span className="code-key">"signature"</span>{`:  `}<span className="code-str">"ed25519:4Bx7R..."</span>{`  `}<span className="code-comment">// device signing key</span>{`
}`}</pre>
);

const TAPCode = () => (
  <pre>{`// TAP attestation certificate (simplified)
{
  `}<span className="code-key">"tap_version"</span>{`:        `}<span className="code-str">"1.0"</span>{`,
  `}<span className="code-key">"issuer"</span>{`:             `}<span className="code-str">"vigil-attestation-service"</span>{`,
  `}<span className="code-key">"subject"</span>{`: {
    `}<span className="code-key">"agent_id"</span>{`:          `}<span className="code-str">"agt_sha256:9f2a..."</span>{`,
    `}<span className="code-key">"provider"</span>{`:         `}<span className="code-str">"anthropic"</span>{`,
    `}<span className="code-key">"behavioral_hash"</span>{`:   `}<span className="code-str">"sha256:1c4d..."</span>{`
  },
  `}<span className="code-key">"delegation_depth"</span>{`:   `}<span className="code-num">1</span>{`,        `}<span className="code-comment">// hops from human</span>{`
  `}<span className="code-key">"human_origin_proof"</span>{`: `}<span className="code-str">"sig:3Rx9..."</span>{`,
  `}<span className="code-key">"valid_until"</span>{`:        `}<span className="code-str">"2026-03-10T10:14:22Z"</span>{`,
  `}<span className="code-key">"revocation_endpoint"</span>{`:`}<span className="code-str">"varp://varp.vigilsec.ai"</span>{`,
  `}<span className="code-key">"cert_signature"</span>{`:     `}<span className="code-str">"ed25519:8Hk2N..."</span>{`
}`}</pre>
);

const VARPCode = () => (
  <pre>{`// VARP revocation notice
POST varp://varp.vigilsec.ai/revoke
{
  `}<span className="code-key">"revoked_agent"</span>{`: `}<span className="code-str">"agt_sha256:9f2a..."</span>{`,
  `}<span className="code-key">"reason"</span>{`:        `}<span className="code-str">"BEHAVIORAL_ANOMALY"</span>{`,  `}<span className="code-comment">// or COMPROMISE | EXPIRED</span>{`
  `}<span className="code-key">"evidence_voaf"</span>{`: `}<span className="code-str">"exp_01HXK7..."</span>{`,        `}<span className="code-comment">// links audit trail</span>{`
  `}<span className="code-key">"cascade"</span>{`:       `}<span className="code-bool">true</span>{`,
  `}<span className="code-key">"issued_by"</span>{`:     `}<span className="code-str">"dvc_sha256:3f4a..."</span>{`,
  `}<span className="code-key">"ts"</span>{`:            `}<span className="code-str">"2026-03-10T09:44:11Z"</span>{`,
  `}<span className="code-key">"signature"</span>{`:    `}<span className="code-str">"ed25519:2Wm5L..."</span>{`
}`}</pre>
);

const VDTCode = () => (
  <pre>{`// VDT classification output for a single interaction
{
  `}<span className="code-key">"action_class"</span>{`:    `}<span className="code-str">"FINANCIAL.TRANSFER"</span>{`,     `}<span className="code-comment">// DOMAIN.SUBTYPE</span>{`
  `}<span className="code-key">"scope"</span>{`:           `}<span className="code-str">"FINANCIAL"</span>{`,
  `}<span className="code-key">"agency_level"</span>{`:    `}<span className="code-num">0.82</span>{`,                     `}<span className="code-comment">// 0.0–1.0 scalar</span>{`
  `}<span className="code-key">"interaction_type"</span>{`:`}<span className="code-str">"DELEGATED"</span>{`,              `}<span className="code-comment">// DIRECT | DELEGATED | AGENT_TO_AGENT | CASCADED</span>{`
  `}<span className="code-key">"intent_vector"</span>{`:   `}<span className="code-str">"[128-dim float array]"</span>{`   `}<span className="code-comment">// semantic embedding</span>{`
}`}</pre>
);

const Specs = () => (
  <section id="specs" data-testid="specs-section">
    <div className="container">
      <div className="specs-header reveal">
        <div>
          <p className="sec-label">Technical Specifications</p>
          <h2 className="specs-headline">Open formats. Proprietary intelligence.</h2>
        </div>
        <p className="specs-intro">The specifications are published without restriction. Any tool, regulator, or developer can read a Vigil audit trail without Vigil installed. The proprietary edge is the detection models and the network effects of the verification layer — not the format itself.</p>
      </div>

      {/* VOAF */}
      <div className="spec-block reveal">
        <div className="spec-layout">
          <div className="spec-meta">
            <p className="spec-abbr">VOAF</p>
            <h3 className="spec-fullname">Vigil Open Audit Format</h3>
            <p className="spec-summary">JSON-based specification for exportable, cryptographically verifiable AI interaction records. Any compliance tool, regulator, or legal counsel can parse a VOAF export without Vigil installed.</p>
            <span className="spec-pill status-live">v1.0 · Live</span>
            <span className="spec-pill">JSON</span>
            <span className="spec-pill">Ed25519</span>
            <p className="spec-pdf-note">Full PDF specification — Q2 2026</p>
          </div>

          <div className="spec-body">
            <p className="spec-section-title">Envelope Structure</p>
            <p className="spec-prose">Every VOAF export is a self-contained JSON envelope containing the full interaction record, a cryptographic signature chain, and a verification manifest that allows offline integrity checking without contacting Vigil servers.</p>

            <div className="code-block">
              <VOAFCode />
            </div>

            <p className="spec-section-title">Core Fields</p>
            <div className="spec-fields">
              <div className="spec-field">
                <span className="spec-field-name">action_class</span>
                <span className="spec-field-type">STRING</span>
                <span className="spec-field-desc">Hierarchical action taxonomy. Format: DOMAIN.SUBTYPE. 200 classes across 8 domains.</span>
              </div>
              <div className="spec-field">
                <span className="spec-field-name">agency_level</span>
                <span className="spec-field-type">FLOAT 0–1</span>
                <span className="spec-field-desc">0.0 = pure information. 1.0 = fully autonomous action with no human in the loop.</span>
              </div>
              <div className="spec-field">
                <span className="spec-field-name">anomaly_score</span>
                <span className="spec-field-type">FLOAT 0–1</span>
                <span className="spec-field-desc">Composite score from three detection models. Above 0.7 triggers an alert.</span>
              </div>
              <div className="spec-field">
                <span className="spec-field-name">prev_hash</span>
                <span className="spec-field-type">SHA-256</span>
                <span className="spec-field-desc">Links to the previous record. Breaks the chain if any record is tampered.</span>
              </div>
              <div className="spec-field">
                <span className="spec-field-name">signature</span>
                <span className="spec-field-type">Ed25519</span>
                <span className="spec-field-desc">Device-local signing key. Proof of origin without centralised PKI.</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* TAP */}
      <div className="spec-block reveal">
        <div className="spec-layout">
          <div className="spec-meta">
            <p className="spec-abbr">TAP</p>
            <h3 className="spec-fullname">Trust Attestation Protocol</h3>
            <p className="spec-summary">When two AI agents interact, TAP lets each verify the other's behavioral integrity before acting on instructions. Designed for the agentic mesh — where AI calls AI calls AI.</p>
            <span className="spec-pill status-live">v1.0 · Live</span>
            <span className="spec-pill">X.509 Compatible</span>
            <p className="spec-pdf-note">Full PDF specification — Q2 2026</p>
          </div>

          <div className="spec-body">
            <p className="spec-section-title">The Problem TAP Solves</p>
            <p className="spec-prose">As AI agents increasingly invoke other agents, trust propagation becomes the attack surface. An instruction passed through four agent hops has no way to verify whether the originating human actually authorized it — or whether it was injected, modified, or fabricated somewhere in the chain. TAP provides cryptographic proof at each hop.</p>

            <div className="code-block">
              <TAPCode />
            </div>

            <p className="spec-section-title">Key Fields</p>
            <div className="spec-fields">
              <div className="spec-field">
                <span className="spec-field-name">delegation_depth</span>
                <span className="spec-field-type">INTEGER</span>
                <span className="spec-field-desc">Number of agent hops from original human intent. 0 = direct human instruction. Unlimited depth tracked.</span>
              </div>
              <div className="spec-field">
                <span className="spec-field-name">human_origin_proof</span>
                <span className="spec-field-type">SIG</span>
                <span className="spec-field-desc">Cryptographic link proving the root instruction came from an authenticated human intent.</span>
              </div>
              <div className="spec-field">
                <span className="spec-field-name">behavioral_hash</span>
                <span className="spec-field-type">SHA-256</span>
                <span className="spec-field-desc">Hash of the agent's behavioral baseline at attestation time. Detects if behavior changed after the certificate was issued.</span>
              </div>
              <div className="spec-field">
                <span className="spec-field-name">revocation_endpoint</span>
                <span className="spec-field-type">VARP URI</span>
                <span className="spec-field-desc">Any agent receiving this certificate can check revocation status in real time via VARP.</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* VARP */}
      <div className="spec-block reveal">
        <div className="spec-layout">
          <div className="spec-meta">
            <p className="spec-abbr">VARP</p>
            <h3 className="spec-fullname">Vigil Agent Revocation Protocol</h3>
            <p className="spec-summary">The equivalent of certificate revocation for the agentic mesh. When a compromised agent is detected, VARP cascades the revocation to every downstream agent holding a trust certificate derived from it.</p>
            <span className="spec-pill status-draft">v0.8 · Draft</span>
            <span className="spec-pill">CRL Compatible</span>
            <p className="spec-pdf-note">Draft open for review — <a href="mailto:standards@vigilsec.ai" style={{color: 'var(--red)'}}>standards@vigilsec.ai</a></p>
          </div>

          <div className="spec-body">
            <p className="spec-section-title">Cascading Revocation</p>
            <p className="spec-prose">A single compromised agent in a four-hop chain can corrupt every downstream decision it influenced. VARP provides a publish-subscribe revocation mechanism — when Agent A is revoked, every agent holding a TAP certificate that chains to Agent A is immediately notified and its certificate invalidated. Revocation propagates in milliseconds, not hours.</p>

            <div className="code-block">
              <VARPCode />
            </div>

            <p className="spec-section-title">Core Fields</p>
            <div className="spec-fields">
              <div className="spec-field">
                <span className="spec-field-name">reason</span>
                <span className="spec-field-type">ENUM</span>
                <span className="spec-field-desc">BEHAVIORAL_ANOMALY — drift or anomaly score exceeded threshold. COMPROMISE — external evidence of tampering. EXPIRED — certificate TTL elapsed.</span>
              </div>
              <div className="spec-field">
                <span className="spec-field-name">evidence_voaf</span>
                <span className="spec-field-type">STRING</span>
                <span className="spec-field-desc">Reference to the VOAF export that contains the evidence for revocation. Allows downstream agents to audit the basis of the revocation decision.</span>
              </div>
              <div className="spec-field">
                <span className="spec-field-name">cascade</span>
                <span className="spec-field-type">BOOL</span>
                <span className="spec-field-desc">When true, revocation propagates to all downstream agents holding certificates that chain to the revoked agent.</span>
              </div>
              <div className="spec-field">
                <span className="spec-field-name">signature</span>
                <span className="spec-field-type">Ed25519</span>
                <span className="spec-field-desc">Signed by the issuing device. Prevents forged revocation notices from being injected into the network.</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* VDT */}
      <div className="spec-block reveal">
        <div className="spec-layout">
          <div className="spec-meta">
            <p className="spec-abbr">VDT</p>
            <h3 className="spec-fullname">Vigil Decomposition Taxonomy</h3>
            <p className="spec-summary">Open taxonomy for classifying AI agent interactions across seven dimensions. Provides a common vocabulary for audit trails, policy engines, and threat analysis that any tool can implement.</p>
            <span className="spec-pill status-live">v1.0 · Live</span>
            <span className="spec-pill">Machine-readable</span>
            <p className="spec-pdf-note">Full PDF specification — Q2 2026</p>
          </div>

          <div className="spec-body">
            <p className="spec-section-title">Purpose</p>
            <p className="spec-prose">VOAF records are only comparable across tools if those tools classify interactions using the same vocabulary. VDT provides that shared vocabulary — eight scope domains, 200 action classes, a scalar agency level, and interaction type classification. Any system that produces VOAF-compatible audit trails should implement VDT classification.</p>

            <div className="code-block">
              <VDTCode />
            </div>

            <p className="spec-section-title">Scope Domains</p>
            <div className="spec-fields">
              <div className="spec-field">
                <span className="spec-field-name">FINANCIAL</span>
                <span className="spec-field-type">Domain</span>
                <span className="spec-field-desc">Portfolio management, banking, transactions, investment decisions, insurance.</span>
              </div>
              <div className="spec-field">
                <span className="spec-field-name">HEALTH</span>
                <span className="spec-field-type">Domain</span>
                <span className="spec-field-desc">Medical records, prescriptions, health decisions, clinical information.</span>
              </div>
              <div className="spec-field">
                <span className="spec-field-name">PROFESSIONAL</span>
                <span className="spec-field-type">Domain</span>
                <span className="spec-field-desc">Work communications, career, business operations, management tasks.</span>
              </div>
              <div className="spec-field">
                <span className="spec-field-name">LEGAL</span>
                <span className="spec-field-type">Domain</span>
                <span className="spec-field-desc">Contracts, agreements, compliance, regulatory filings.</span>
              </div>
              <div className="spec-field">
                <span className="spec-field-name">IDENTITY</span>
                <span className="spec-field-type">Domain</span>
                <span className="spec-field-desc">Authentication, personal data, credentials, privacy.</span>
              </div>
              <div className="spec-field">
                <span className="spec-field-name">agency_level</span>
                <span className="spec-field-type">FLOAT 0–1</span>
                <span className="spec-field-desc">0.0 = pure information. 0.4–0.6 = recommendation. 0.8–0.9 = action with implicit approval. 1.0 = fully autonomous.</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const Lab = () => (
  <section id="lab" data-testid="lab-section">
    <div className="container">
      <div className="lab-header reveal">
        <div>
          <p className="lab-kicker">Vigil Security Lab</p>
          <h2 className="lab-headline">Original analysis on AI agent threat surfaces.</h2>
        </div>
        <div>
          <p className="lab-desc">The Lab publishes threat analysis, proposed detection methodologies, and risk indices for AI agent security. All research is original analysis based on publicly observable patterns in AI agent deployments. Reports ship Q2 2026.</p>
        </div>
      </div>

      <div className="lab-reports">
        <div className="lab-report reveal">
          <div className="report-type">
            <span className="report-dot soon"></span>Threat Analysis
          </div>
          <h3 className="report-title">Prompt Injection via Agent Delegation Chains</h3>
          <p className="report-summary">Analysis of how malicious instructions propagate through multi-agent systems when trust attestation is absent. Proposed detection heuristics based on delegation depth and behavioral drift correlation.</p>
          <div className="report-meta">
            <span className="report-date">Q2 2026</span>
            <span className="report-tag">Coming Soon</span>
          </div>
        </div>

        <div className="lab-report reveal">
          <div className="report-type">
            <span className="report-dot soon"></span>Threat Index
          </div>
          <h3 className="report-title">Vigil Threat Index Q1 2026: AI Agent Attack Surface</h3>
          <p className="report-summary">Proposed taxonomy of AI agent attack classes across financial, health, and professional surfaces. Agency-level distribution analysis across 200 action classes as a proposed detection baseline methodology.</p>
          <div className="report-meta">
            <span className="report-date">Q2 2026</span>
            <span className="report-tag">Coming Soon</span>
          </div>
        </div>

        <div className="lab-report reveal">
          <div className="report-type">
            <span className="report-dot upcoming"></span>Upcoming
          </div>
          <h3 className="report-title">Behavioral Drift as a Leading Indicator of AI Agent Compromise</h3>
          <p className="report-summary">Analysis of how gradual behavioral change precedes catastrophic agent failure. Proposed CUSUM-based detection methodology and threshold frameworks for financial-scope agents.</p>
          <div className="report-meta">
            <span className="report-date">Q3 2026</span>
            <span className="report-tag">Coming Soon</span>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const Regulatory = () => (
  <section id="regulatory" data-testid="regulatory-section">
    <div className="container">
      <div className="regulatory-layout">
        <div className="regulatory-sticky">
          <p className="sec-label">Regulatory Alignment</p>
          <h2 className="regulatory-headline">Designed to be the reference implementation.</h2>
          <p className="regulatory-body">AI accountability regulation is crystallizing globally. VOAF and TAP are designed to map to the audit trail and oversight requirements taking shape across jurisdictions. The goal is to have the technical standard established before regulatory language finalizes — so that Vigil's architecture becomes the reference, not a response.</p>
          <div className="nist-badge">
            <span className="nist-badge-top">NIST RFI Submission</span>
            <span className="nist-badge-val">Docket NIST-2025-0035</span>
            <span className="nist-badge-track">Tracking: mmk-190r-hvap · March 9, 2026</span>
          </div>
        </div>

        <div className="reg-items reveal">
          <div className="reg-item">
            <div>
              <p className="reg-item-name">EU AI Act</p>
              <p className="reg-item-desc">Full enforcement August 2026. High-risk AI systems will require audit trails and human oversight documentation. VOAF is designed to align with those audit trail requirements.</p>
            </div>
            <span className="reg-item-status upcoming">Aug 2026</span>
          </div>
          <div className="reg-item">
            <div>
              <p className="reg-item-name">NIST AI Risk Management Framework</p>
              <p className="reg-item-desc">Vigil's six-layer architecture submitted to NIST RFI Docket 2025-0035 as a proposed reference architecture for AI agent accountability infrastructure. Submission on record, pending review.</p>
            </div>
            <span className="reg-item-status submitted">Submitted</span>
          </div>
          <div className="reg-item">
            <div>
              <p className="reg-item-name">IMDA Model AI Governance Framework</p>
              <p className="reg-item-desc">Singapore's January 2026 framework for agentic AI governance. Vigil's architecture is designed to support the accountability and transparency principles outlined in the IMDA framework.</p>
            </div>
            <span className="reg-item-status active">Active</span>
          </div>
          <div className="reg-item">
            <div>
              <p className="reg-item-name">CSA Agentic AI Security Addendum</p>
              <p className="reg-item-desc">Cloud Security Alliance guidance on agentic AI threat modeling. TAP addresses the agent-to-agent trust verification concerns raised in the addendum.</p>
            </div>
            <span className="reg-item-status active">Active</span>
          </div>
          <div className="reg-item">
            <div>
              <p className="reg-item-name">Colorado Algorithmic Accountability Act</p>
              <p className="reg-item-desc">Effective February 2026. Requires documentation of consequential AI decision processes. VOAF exports are structured to support the documentation requirements under the Act.</p>
            </div>
            <span className="reg-item-status active">Active</span>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const OpenSource = () => (
  <section id="opensource" data-testid="opensource-section">
    <div className="container">
      <div className="oss-header reveal">
        <div>
          <p className="sec-label">Open Source</p>
          <h2 className="oss-headline">Verification tools that outlast any company.</h2>
        </div>
        <p className="oss-body">If Vigil ceases to exist tomorrow, every audit trail it ever produced remains verifiable. The verification tools are open source, dependency-free, and run offline. That is the point of open standards — they survive the company that created them. All tools publish Q2 2026.</p>
      </div>

      <div className="oss-tools">
        <div className="oss-tool reveal">
          <div className="oss-tool-header">
            <p className="oss-tool-name">vigil-verify</p>
            <span className="oss-tool-badge">Q2 2026</span>
          </div>
          <p className="oss-tool-desc">Command-line tool for verifying VOAF export integrity. Checks hash chain continuity, Ed25519 signature validity, and schema conformance. Zero dependencies. Runs offline.</p>
          <div className="oss-tool-pending">Available Q2 2026 · cargo install vigil-verify</div>
        </div>
        <div className="oss-tool reveal">
          <div className="oss-tool-header">
            <p className="oss-tool-name">voaf-schema</p>
            <span className="oss-tool-badge">Q2 2026</span>
          </div>
          <p className="oss-tool-desc">JSON Schema definition for VOAF v1.0. Validate any VOAF export in your own tooling. Available as NPM package, Python package, and raw JSON Schema file.</p>
          <div className="oss-tool-pending">Available Q2 2026 · npm install @vigil/voaf-schema</div>
        </div>
        <div className="oss-tool reveal">
          <div className="oss-tool-header">
            <p className="oss-tool-name">tap-verify</p>
            <span className="oss-tool-badge">Q2 2026</span>
          </div>
          <p className="oss-tool-desc">Verify TAP attestation certificates and check revocation status via VARP. Supports offline validation with cached revocation lists.</p>
          <div className="oss-tool-pending">Available Q2 2026 · cargo install tap-verify</div>
        </div>
        <div className="oss-tool reveal">
          <div className="oss-tool-header">
            <p className="oss-tool-name">vdt-taxonomy</p>
            <span className="oss-tool-badge">Q2 2026</span>
          </div>
          <p className="oss-tool-desc">The Vigil Decomposition Taxonomy as a machine-readable dataset. 200 action classes across 8 scope domains with hierarchical classification rules.</p>
          <div className="oss-tool-pending">Available Q2 2026 · pip install vdt-taxonomy</div>
        </div>
      </div>

      <p className="oss-footer-note">All tools will be published on GitHub under Apache 2.0 · github.com/vigilsec</p>
    </div>
  </section>
);

const Contact = () => (
  <section id="contact" data-testid="contact-section">
    <div className="container">
      <div className="contact-inner">
        <div>
          <p className="sec-label">Contact</p>
          <h2 className="contact-headline">For regulators, developers, and anyone building on the standards.</h2>
          <p className="contact-body">Adoption questions, VARP draft review, regulatory mapping for your jurisdiction, or press and analyst inquiries. We respond to all standards-related correspondence.</p>
        </div>
        <div className="contact-cards">
          <div className="contact-card">
            <span className="contact-card-label">Standards</span>
            <a href="mailto:standards@vigilsec.ai" className="contact-card-value" data-testid="contact-standards">standards@vigilsec.ai</a>
          </div>
          <div className="contact-card">
            <span className="contact-card-label">Regulatory</span>
            <a href="mailto:regulatory@vigilsec.ai" className="contact-card-value" data-testid="contact-regulatory">regulatory@vigilsec.ai</a>
          </div>
          <div className="contact-card">
            <span className="contact-card-label">Press</span>
            <a href="mailto:press@vigilsec.ai" className="contact-card-value" data-testid="contact-press">press@vigilsec.ai</a>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const About = () => (
  <section id="about" data-testid="about-section">
    <div className="container">
      <div className="about-layout">
        <div>
          <p className="about-kicker">About Vigil Security</p>
          <h2 className="about-headline">Infrastructure, not a product.</h2>
          <p className="about-body">Vigil Security was founded on the thesis that AI agent accountability infrastructure is an inevitable category — the same way SSL/TLS became inevitable for the web, and Visa became inevitable for payments. We are building the technical standard before regulation forces it to exist.</p>
          <p className="about-body">Open standards create ecosystem adoption. That adoption creates network effects no competitor can replicate from a standing start. The specifications published here are free to use without restriction.</p>
          <p className="about-body">NIST submission on record. IMDA framework alignment in progress.</p>
        </div>

        <div className="about-right">
          <div className="about-principle">
            <p className="about-principle-title">Statistical models over LLMs in the detection path</p>
            <p className="about-principle-body">Avoids circular trust dependencies. Deterministic and auditable. Not susceptible to the same adversarial inputs as the systems it monitors.</p>
          </div>
          <div className="about-principle">
            <p className="about-principle-title">Provider-neutral by design</p>
            <p className="about-principle-body">VOAF and TAP work across every AI provider. The standard is not tied to Anthropic, OpenAI, Google, or any other platform. Accountability infrastructure that only works on one provider is not infrastructure.</p>
          </div>
          <div className="about-principle">
            <p className="about-principle-title">Offline verifiable</p>
            <p className="about-principle-body">Every audit trail produced by Vigil can be verified without contacting Vigil's servers. The cryptographic chain, the signing keys, and the open source tools are all you need.</p>
          </div>
          <div className="about-principle">
            <p className="about-principle-title">Original analysis only</p>
            <p className="about-principle-body">Security Lab research is based on publicly observable patterns. We analyze and propose methodologies — not assert findings we have not observed. Credibility with regulators and the research community is not negotiable.</p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const Footer = () => (
  <footer data-testid="footer">
    <div className="container">
      <div className="footer-inner">
        <div className="footer-brand">
          <span className="footer-wordmark">Vigil Security</span>
          <span className="footer-tagline">Open Standards for AI Agent Accountability</span>
        </div>
        <div className="footer-cols">
          <div>
            <p className="footer-col-title">Standards</p>
            <ul className="footer-links">
              <li><a href="#specs">VOAF v1.0</a></li>
              <li><a href="#specs">TAP v1.0</a></li>
              <li><a href="#specs">VARP v0.8 Draft</a></li>
              <li><a href="#specs">VDT v1.0</a></li>
            </ul>
          </div>
          <div>
            <p className="footer-col-title">Research</p>
            <ul className="footer-links">
              <li><a href="#lab">Security Lab</a></li>
              <li><a href="#regulatory">Regulatory</a></li>
              <li><a href="#opensource">Open Source</a></li>
            </ul>
          </div>
          <div>
            <p className="footer-col-title">Contact</p>
            <ul className="footer-links">
              <li><a href="mailto:standards@vigilsec.ai">standards@vigilsec.ai</a></li>
              <li><a href="mailto:regulatory@vigilsec.ai">regulatory@vigilsec.ai</a></li>
              <li><a href="mailto:press@vigilsec.ai">press@vigilsec.ai</a></li>
            </ul>
          </div>
        </div>
      </div>
      <div className="footer-bottom">
        <p className="footer-copy">© 2026 Vigil Security</p>
      </div>
    </div>
  </footer>
);

function App() {
  const revealRefs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add('in');
          observer.unobserve(e.target);
        }
      });
    }, { threshold: 0.1 });

    const revealElements = document.querySelectorAll('.reveal');
    revealElements.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <div className="App" data-testid="vigil-app">
      <Navigation />
      <Masthead />
      <Specs />
      <Lab />
      <Regulatory />
      <OpenSource />
      <Contact />
      <About />
      <Footer />
    </div>
  );
}

export default App;
